"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MessageSquareCode, Search, Compass, Info } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function AiPlatforms() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="aiplatformsvisibility" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 rounded-md text-xs font-semibold tracking-wide uppercase">
            Platform Comparison
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground">
            ChatGPT, Google AI and Perplexity: How Each Platform Differs
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Platforms differ in how they crawl, retrieve, summarise and cite, so I test prompts across several platforms for your niche instead of assuming one universal rule.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Card 1: ChatGPT */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="h-full w-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl p-6 flex flex-col justify-between">
              <div>
                <CardHeader className="p-0 pb-5 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 w-fit">
                      <MessageSquareCode className="size-6 text-primary" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground font-medium">01</span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                    ChatGPT SEO and ChatGPT Search
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 pt-5 space-y-4 text-sm sm:text-base leading-relaxed text-muted-foreground">
                  <p>
                    ChatGPT uses web retrieval to ground answers. Crucially, retrieval and citation are separate steps: many pages are retrieved during generation, but fewer are selected for cited source links.
                  </p>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Practical foundations</h4>
                    <ul className="space-y-2 text-xs sm:text-sm">
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Key pages must be publicly accessible (not blocked by robots.txt or logins, crawlable by OpenAI&apos;s search crawler).</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Content should answer questions buyers actually ask in short, clear paragraphs.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Brand details (services, coverage, credentials) must be straightforward to parse.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Claims should be supported by dates and named sources. Third-party references (reviews, directories, media mentions) can also matter.</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </div>
              <div className="mt-6 pt-4 border-t border-border/80 text-xs text-muted-foreground italic">
                No consultant can promise consistent ChatGPT citations; the same prompt can produce different sources in different sessions.
              </div>
            </Card>
          </motion.div>

          {/* Card 2: Google AI */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="h-full w-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl p-6 flex flex-col justify-between">
              <div>
                <CardHeader className="p-0 pb-5 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 w-fit">
                      <Search className="size-6 text-primary" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground font-medium">02</span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                    Google AI Overviews and AI Mode
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 pt-5 space-y-4 text-sm sm:text-base leading-relaxed text-muted-foreground">
                  <p>
                    These features sit directly within Google Search, using Google&apos;s index and core ranking systems alongside a generative layer. Supporting citation links are drawn straight from indexed results.
                  </p>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Practical foundations</h4>
                    <ul className="space-y-2 text-xs sm:text-sm">
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Crawlability, indexability, topical relevance, and helpful people-first content (E-E-A-T principles) remain the core base.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Answering queries clearly near the top of the page assists both snippet extraction and overview synthesis.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>
                          Ensuring technical integrity through{" "}
                          <Link to="/TechnicalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
                            technical SEO
                          </Link>{" "}
                          and periodic{" "}
                          <Link to="/SeoAuditsPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
                            SEO audits
                          </Link>{" "}
                          removes crawl obstacles.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Structured data helps interpretation, but there is no special &ldquo;AI Overview schema&rdquo; that guarantees selection.</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </div>
              <div className="mt-6 pt-4 border-t border-border/80 text-xs text-muted-foreground italic">
                Google AI Overviews build on organic search rankings; strong traditional SEO remains the foundation.
              </div>
            </Card>
          </motion.div>

          {/* Card 3: Perplexity & Copilot */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="h-full w-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl p-6 flex flex-col justify-between">
              <div>
                <CardHeader className="p-0 pb-5 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 w-fit">
                      <Compass className="size-6 text-primary" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground font-medium">03</span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                    Perplexity, Bing Copilot and other platforms
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 pt-5 space-y-4 text-sm sm:text-base leading-relaxed text-muted-foreground">
                  <p>
                    Perplexity-style engines show several citations per answer and lean heavily on well-structured, current, and credible sources.
                  </p>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Practical foundations</h4>
                    <ul className="space-y-2 text-xs sm:text-sm">
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Bing Copilot relies on Bing&apos;s index, meaning solid technical SEO and content quality directly carry over.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Some generative tools draw on curated niche sources (directories, trade publications, product feeds), so keeping those accurate matters.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>Clear dates, author attribution, and source citations give models confidence when synthesising current comparisons.</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </div>
              <div className="mt-6 pt-4 border-t border-border/80 text-xs text-muted-foreground italic">
                Active multi-engine monitoring helps identify which platforms naturally cite your brand and where gaps exist.
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Closing Note Banner */}
        <div className="mt-12 p-6 rounded-xl border border-border bg-muted/40 max-w-4xl mx-auto flex items-start gap-4">
          <Info className="size-5 text-primary shrink-0 mt-0.5" />
          <div className="space-y-1 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              Cross-platform insight
            </p>
            <p>
              What earns a mention on one platform may differ on another; stronger clarity, technical health and credible mentions tend to help traditional SEO and AI discovery together.
            </p>
          </div>
        </div>
      </div>
    </section>;
}